import type { MatchPhase, MatchStatus } from '@brochure/shared'
import { countAirballsByPlayer, countAirballsByTeam, getCupsForPhase } from '@brochure/shared'

/** Badge apertura/chiusura: assegnato quando il giocatore ha aperto/chiuso almeno il 30% delle partite giocate. */
const OPENER_CLOSER_THRESHOLD_RATIO = 0.3

export type StatsMatch = {
  id: string
  phase: MatchPhase | string
  round: number
  sortOrder: number
  homeTeamId: string
  awayTeamId: string
  homeScore: number | null
  awayScore: number | null
  status: MatchStatus | string
}

export type StatsTeam = { id: string; players: { id: string; name: string }[] }

/** Sottoinsieme di `TournamentDetail` che le statistiche leggono davvero: `TournamentDetail` lo soddisfa
 * per tipizzazione strutturale, i test possono costruire fixture minime senza cast. */
export type StatsDetail = {
  tournament: { cupsPerTeamGroup: number; cupsPerTeamFinal: number }
  teams: StatsTeam[]
  matches: StatsMatch[]
}

export type CupEventInput = { matchId: string; scorerId: string; cupIndex: number; isRedemption: boolean; second: number }
export type AirballInput = { matchId: string; playerId: string; teamId: string }

/** Sottoinsieme di `GET /tournaments/:id/events` usato dalle statistiche (le rimappe non servono qui). */
export type StatsEvents = { cupEvents: readonly CupEventInput[]; airballs: readonly AirballInput[] }

export type TeamStats = {
  teamId: string
  played: number
  wins: number
  losses: number
  cupsFor: number
  cupsAgainst: number
  diff: number
  avgFor: number
  bestStreak: number
  redemptions: number
  airballs: number
}

export type PlayerStats = {
  playerId: string
  cups: number
  openers: number
  closers: number
  openerBadge: boolean
  closerBadge: boolean
  heatmap: number[]
  favorite: number | null
  avoided: number | null
  average: number
  airballs: number
}

/** Righe del triangolo dei bicchieri, dalla base alla punta. */
export function heatmapLayout(count: 3 | 6): number[][] {
  return count === 3 ? [[0, 1], [2]] : [[0, 1, 2], [3, 4], [5]]
}

/** Striscia di vittorie consecutive più lunga, nell'ordine passato (già ordinato per `round`/`sortOrder`). */
function longestWinStreak(orderedMatches: readonly StatsMatch[], teamId: string): number {
  let streak = 0
  let best = 0
  for (const m of orderedMatches) {
    const isHome = m.homeTeamId === teamId
    const teamScore = (isHome ? m.homeScore : m.awayScore) ?? 0
    const oppScore = (isHome ? m.awayScore : m.homeScore) ?? 0
    if (teamScore > oppScore) {
      streak += 1
      best = Math.max(best, streak)
    } else {
      streak = 0
    }
  }
  return best
}

/**
 * Statistiche per squadra: vittorie/sconfitte dalle partite concluse (qualsiasi fase), striscia di vittorie
 * più lunga nell'ordine di gioco, redenzioni segnate dai propri giocatori, airball totali (qualsiasi fase).
 */
export function teamStats(detail: StatsDetail, events: StatsEvents): TeamStats[] {
  const orderedMatches = [...detail.matches].sort((a, b) => a.round - b.round || a.sortOrder - b.sortOrder)
  const airballsByTeam = countAirballsByTeam(events.airballs)

  return detail.teams.map((team) => {
    const teamMatches = orderedMatches.filter((m) => m.status === 'completed' && (m.homeTeamId === team.id || m.awayTeamId === team.id))

    let wins = 0
    let losses = 0
    let cupsFor = 0
    let cupsAgainst = 0
    for (const m of teamMatches) {
      const isHome = m.homeTeamId === team.id
      const teamScore = (isHome ? m.homeScore : m.awayScore) ?? 0
      const oppScore = (isHome ? m.awayScore : m.homeScore) ?? 0
      cupsFor += teamScore
      cupsAgainst += oppScore
      if (teamScore > oppScore) wins += 1
      else losses += 1
    }

    const playerIds = new Set(team.players.map((p) => p.id))
    const redemptions = events.cupEvents.filter((e) => e.isRedemption && playerIds.has(e.scorerId)).length

    return {
      teamId: team.id,
      played: teamMatches.length,
      wins,
      losses,
      cupsFor,
      cupsAgainst,
      diff: cupsFor - cupsAgainst,
      avgFor: teamMatches.length > 0 ? cupsFor / teamMatches.length : 0,
      bestStreak: longestWinStreak(teamMatches, team.id),
      redemptions,
      airballs: airballsByTeam.get(team.id) ?? 0,
    }
  })
}

/**
 * Statistiche per giocatore nel formato dato (3 o 6 bicchieri): partite della propria squadra in quel
 * formato (`getCupsForPhase`), apertura/chiusura calcolate sull'ordine di tutti gli eventi della partita
 * (non solo i propri), bicchiere preferito/evitato dalla heatmap, media a partita, airball nel formato.
 */
export function playerStats(detail: StatsDetail, events: StatsEvents, cupCount: 3 | 6): PlayerStats[] {
  const settings = detail.tournament
  const result: PlayerStats[] = []

  for (const team of detail.teams) {
    const fieldMatches = detail.matches.filter(
      (m) =>
        (m.status === 'completed' || m.status === 'live') &&
        (m.homeTeamId === team.id || m.awayTeamId === team.id) &&
        getCupsForPhase(m.phase, settings) === cupCount,
    )
    const matchIds = new Set(fieldMatches.map((m) => m.id))
    const airballsByPlayer = countAirballsByPlayer(events.airballs.filter((a) => matchIds.has(a.matchId)))
    const threshold = Math.max(1, fieldMatches.length * OPENER_CLOSER_THRESHOLD_RATIO)

    for (const player of team.players) {
      const playerEvents = events.cupEvents.filter((e) => e.scorerId === player.id && matchIds.has(e.matchId))
      const heatmap = new Array(cupCount).fill(0)
      for (const e of playerEvents) if (e.cupIndex < cupCount) heatmap[e.cupIndex] += 1

      let openers = 0
      let closers = 0
      for (const matchId of matchIds) {
        const order = events.cupEvents
          .filter((e) => e.matchId === matchId && !e.isRedemption)
          .sort((a, b) => a.second - b.second || a.cupIndex - b.cupIndex)
        if (order.length === 0) continue
        if (order[0].scorerId === player.id) openers += 1
        if (order[order.length - 1].scorerId === player.id) closers += 1
      }

      const maxHits = Math.max(...heatmap)
      const favorite = maxHits > 0 ? heatmap.indexOf(maxHits) : null
      const nonZero = heatmap.filter((h) => h > 0)
      const minHits = nonZero.length > 0 ? Math.min(...nonZero) : null
      const avoidedIndex = minHits !== null ? heatmap.indexOf(minHits) : -1
      const avoided = avoidedIndex >= 0 && avoidedIndex !== favorite ? avoidedIndex : null

      result.push({
        playerId: player.id,
        cups: playerEvents.length,
        openers,
        closers,
        openerBadge: openers > 0 && openers >= threshold,
        closerBadge: closers > 0 && closers >= threshold,
        heatmap,
        favorite,
        avoided,
        average: fieldMatches.length > 0 ? playerEvents.length / fieldMatches.length : 0,
        airballs: airballsByPlayer.get(player.id) ?? 0,
      })
    }
  }

  return result
}
